import React from 'react';
import { X, MapPin, Ruler } from 'lucide-react';
import TrackMap3D from './TrackMap3D';
import CountryFlag from './CountryFlag';

// Track metadata + ordered corner names (sourced from the circuit SVGs).
const TRACK_INFO = {
  nordschleife: {
    location: 'Nürburg, Germany',
    length: '20.832 km',
    blurb:
      'Nicknamed the "Green Hell", the Nordschleife is widely considered the most demanding purpose-built racing circuit in the world — 73 corners carved through the Eifel forest, with barely a moment of rest across its 20 km.',
    corners: [
      'Tiergarten', 'Hohenrain', 'Hatzenbach', 'Hoheneichen', 'Quiddelbacher Höhe',
      'Flugplatz', 'Schwedenkreuz', 'Aremberg', 'Fuchsröhre', 'Adenauer Forst',
      'Metzgesfeld', 'Kallenhard', 'Wehrseifen', 'Exmühle', 'Bergwerk',
      'Kesselchen', 'Klostertal', 'Steilstrecke', 'Caracciola-Karussell', 'Hohe Acht',
      'Hedwigshöhe', 'Wippermann', 'Eschbach', 'Brünnchen', 'Pflanzgarten I',
      'Schwalbenschwanz', 'Kleines Karussell', 'Pflanzgarten II', 'Galgenkopf', 'Döttinger Höhe',
    ],
  },
  spa: {
    location: 'Francorchamps, Belgium',
    length: '7.004 km',
    blurb:
      'A legendary flowing layout through the Ardennes forest, Spa is the longest circuit on the modern calendar. Famous for the flat-out Eau Rouge–Raidillon sweep and its notoriously changeable weather.',
    corners: [
      'La Source', 'Eau Rouge', 'Raidillon', 'Kemmel Straight', 'Les Combes',
      'Bruxelles', 'Pouhon', 'Campus', 'Stavelot', 'Courbe Paul Frère',
      'Blanchimont', 'Bus Stop Chicane',
    ],
  },
  silverstone: {
    location: 'Northamptonshire, UK',
    length: '5.891 km',
    blurb:
      'Home of the British Grand Prix, built on a WWII airfield. Fast and flowing, defined by the Maggotts–Becketts–Chapel complex and the iconic Copse and Club corners.',
    corners: [
      'Woodcote', 'Copse', 'Maggotts', 'Becketts', 'Chapel',
      'Hangar Straight', 'Stowe', 'Vale', 'Club', 'Abbey',
      'Bridge', 'Priory', 'Luffield',
    ],
  },
};

export default function TrackMapModal({ trackKey, title, kicker, subtitle, accent, accentText, flag, onClose }) {
  const info = TRACK_INFO[trackKey] || {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-[#0a0b0e] border border-white/10 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="flex items-start justify-between p-5 border-b border-white/10">
          <div>
            <p className={`${accentText} text-[10px] font-black tracking-[0.3em] mb-1`}>{kicker}</p>
            <h3 className="text-white font-black text-xl uppercase tracking-tight leading-none flex items-center gap-2">
              <CountryFlag code={flag} size={20} /> {title}
            </h3>
            <p className="text-white/40 text-xs mt-1.5">{subtitle}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white p-1.5 hover:bg-white/5 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* large map */}
        <div className="p-6 flex justify-center bg-gradient-to-b from-white/[0.02] to-transparent">
          <TrackMap3D trackKey={trackKey} accent={accent} width={460} height={288} />
        </div>

        {/* info */}
        <div className="px-6 pb-6">
          {info.location && (
            <div className="flex flex-wrap items-center gap-4 text-xs mb-4">
              <span className="flex items-center gap-1.5 text-white/60">
                <MapPin className="w-3.5 h-3.5" style={{ color: accent }} /> {info.location}
              </span>
              <span className="flex items-center gap-1.5 text-white/60">
                <Ruler className="w-3.5 h-3.5" style={{ color: accent }} /> {info.length}
              </span>
            </div>
          )}
          {info.blurb && <p className="text-white/60 text-sm leading-relaxed">{info.blurb}</p>}

          {info.corners && (
            <>
              <p className="text-white/40 text-[10px] font-bold tracking-widest uppercase mt-6 mb-3">
                Corners · {info.corners.length}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {info.corners.map((c, i) => (
                  <div key={c} className="flex items-center gap-2.5 border border-white/10 bg-white/[0.02] px-3 py-2">
                    <span className="font-mono text-[10px] font-bold tabular-nums" style={{ color: accent }}>
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="text-white/75 text-xs">{c}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}